// All content, trademarks, and data on this document are the property of Healthworks Analytics, LLC and are protected by applicable intellectual property laws. Unauthorized use, reproduction, or distribution of this material is strictly prohibited.
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { LuSearch, LuMinus, LuPlus, LuMaximize2, LuMinimize2 } from "react-icons/lu";
import { MdChevronLeft, MdClose, MdOutlineImage, MdRefresh, MdZoomIn, MdZoomOut } from "react-icons/md";
import { TransformWrapper, TransformComponent, useControls } from "react-zoom-pan-pinch";
import { ChevronRightIcon } from "../icons";
import RailTip from "../tooltip/RailTip";
import { submitFeedBack } from "../../services/feedback.service";
import { invokeError } from "../../store/auth/action";
import { getSession } from "../../utils/sessionstore";
import "./richTextEditor.css";

function ImageZoomControls() {
  const { zoomIn, zoomOut, resetTransform } = useControls();
  return (
    <div className="absolute scale-95 opacity-85 hover:opacity-100 hover:scale-100 ani bottom-2 bg-general-background rounded-lg flex flex-row gap-2 outline outline-1 outline-general-border shadow-[0px_2px_8px_0px_rgba(0,0,0,0.12)] px-3 py-1 h-max">
      <MdZoomIn className="cursor-pointer text-[20px] text-general-foreground hover:text-general-primary hover:scale-105 ani" onClick={() => zoomIn()} />
      <MdZoomOut className="cursor-pointer text-[20px] text-general-foreground hover:text-general-primary hover:scale-105 ani" onClick={() => zoomOut()} />
      <MdRefresh className="cursor-pointer text-[20px] text-general-foreground hover:text-general-primary hover:scale-105 ani" onClick={() => resetTransform()} />
    </div>
  );
}

const NavRow = ({ text, iconUrl, labelClassName, rowClassName, onClick, children }) => {
  const [isTruncated, setIsTruncated] = useState(false);

  return (
    <RailTip title={text} placement="right" show={isTruncated}>
      <div onClick={onClick} className={rowClassName}>
        {iconUrl && (
          <img
            className="size-4 shrink-0 object-contain"
            src={iconUrl}
            alt=""
            onError={(e) => {
              e.target.onerror = null;
              e.target.style.visibility = "hidden";
            }}
          />
        )}
        <div
          ref={(node) => {
            if (node) setIsTruncated(node.scrollWidth > node.clientWidth);
          }}
          className={labelClassName}
        >
          {text}
        </div>
        {children}
      </div>
    </RailTip>
  );
};

const HWAI_ICON_URL = "https://hwlobbystorer1.blob.core.windows.net/sectionicons/hwaiicon.png?v=1";

const ZOOM_STEP = 10;
const ZOOM_MIN = 50;
const ZOOM_MAX = 150;

// Renders one chapter/section's real content blocks (images + rich text paragraphs)
// exactly as returned by the Help Doc API - no placeholder/hardcoded copy. Mirrors the
// original HelpGuide's content markup 1:1 so behavior (image click-to-maximize, quill
// rich text) stays identical - only the surrounding chrome is the new design.
const ContentBlocks = ({ contents, onMaximizeImage }) => {
  if (!contents?.length) return null;
  return (
    <>
      {contents.map((content, index) => (
        <div key={content?.id || index} className="mt-4 w-full flex flex-col items-center">
          {content?.type === "image" && content?.imageUrl && content?.imageUrl.trim() !== "" && (
            <img
              onClick={() => onMaximizeImage(content)}
              src={content?.imageUrl}
              className="cursor-pointer h-auto"
              style={{
                maxWidth: `${content?.imageSize === "small" ? "30%" : content?.imageSize === "medium" ? "60%" : "100%"}`,
              }}
              alt={content?.imageLabel || "Content image"}
            />
          )}
          {content?.type === "image" && content?.imageLabel && (
            <p className="w-full text-center text-xs uppercase text-general-muted-foreground mt-2">
              {content?.imageLabel}
            </p>
          )}
          {content?.type === "paragraph" && content?.richText && (
            <div className="ql-container ql-snow w-full ql-container-override h-max mt-4">
              <div
                className="ql-editor ql-editor-override h-max text-sm text-general-foreground"
                dangerouslySetInnerHTML={{ __html: content?.richText || "<p></p>" }}
              />
            </div>
          )}
        </div>
      ))}
    </>
  );
};

export default function HelpGuide({
  sectionLog,
  open,
  setOpen,
  data,
  setData,
  chapterLog,
  setChapterLog,
}) {
  const location = useLocation();
  const { clientID } = useParams();
  const dispatch = useDispatch();
  const isOnDashboard = location.pathname.includes("/dashboard");
  const { activedashboard, userDetails, clientname } = useSelector((state) => ({
    activedashboard: state.activedashboard,
    userDetails: state.userDetails,
    clientname: state.clientname,
  }));

  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [zoom, setZoom] = useState(100);
  const [maximized, setMaximized] = useState(!isOnDashboard);
  const [helpful, setHelpful] = useState(null);

  const submitHelpfulFeedback = async (answer) => {
    if (helpful) return;
    setHelpful(answer);
    try {
      await submitFeedBack({
        token: getSession("session_hwai"),
        userId: userDetails?.userId,
        fields: [
          {
            name: selected?.chapterLabel || activedashboard?.dashboardname || "Help Guide",
            stars: answer === "yes" ? 1 : 0,
          },
        ],
        config: {
          helpful: answer,
          source: "HELP_GUIDE",
          dashboardId: activedashboard?._id,
          dashboardName: activedashboard?.dashboardname,
          chapter: selected?.chapterLabel,
          section: selected?.sectionLabel,
          clientId: clientID ?? getSession("client_id"),
          clientName: clientname,
        },
      });
    } catch (err) {
      setHelpful(null);
      dispatch(invokeError(err?.message || "Couldn't submit feedback, please try again."));
    }
  };

  // HelpGuide is mounted once (persistently, from SideBar), so the useState above only
  // captures the route at first app load. Re-sync the default each time it's actually
  // opened, so it reflects whichever page you're on right now.
  useEffect(() => {
    if (open) setMaximized(!isOnDashboard);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const [maximizedImage, setMaximizedImage] = useState(null);
  const [maximizedImageLabel, setMaximizedImageLabel] = useState(null);
  const [maximizedImageSize, setMaximizedImageSize] = useState("large");
  const sectionRefs = useRef({});

  const handleMaximizeImage = (content) => {
    if (maximizedImage === content.imageUrl) {
      setMaximizedImage(null);
      setMaximizedImageLabel(null);
      setMaximizedImageSize("large");
    } else {
      setMaximizedImage(content.imageUrl);
      setMaximizedImageLabel(content.imageLabel);
      setMaximizedImageSize(content.imageSize);
    }
  };

  const chapters = data?.chapters || [];

  const scrollToSection = (id) => {
    sectionRefs.current[id]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const filteredChapters = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return chapters;
    return chapters
      .map((chapter) => {
        const chapterMatches = chapter?.chapterTitle?.toLowerCase().includes(term);
        const sections = (chapter?.sections || []).filter(
          (section) =>
            section?.sectionTitle?.toLowerCase().includes(term) ||
            section?.contents?.some((content) => content?.richText?.toLowerCase().includes(term))
        );
        if (chapterMatches || sections.length > 0) {
          return { ...chapter, sections: chapterMatches ? chapter.sections : sections };
        }
        return null;
      })
      .filter(Boolean);
  }, [search, chapters]);

  const selectChapter = (chapter) => {
    setSelected({ chapterId: chapter?.id, chapterLabel: chapter?.chapterTitle, sectionId: null, sectionLabel: null });
    scrollToSection(chapter?.id);
    if (chapter?.sections?.length > 0) {
      const updatedChapters = data?.chapters?.map((item) =>
        item?.id === chapter?.id ? { ...item, collapsed: !item?.collapsed } : item
      );
      if (chapter?.collapsed) chapterLog({ chapter: chapter?.chapterTitle });
      setData({ ...data, chapters: updatedChapters });
    }
  };

  const selectSection = (chapter, section) => {
    setSelected({
      chapterId: chapter?.id,
      chapterLabel: chapter?.chapterTitle,
      sectionId: section?.id,
      sectionLabel: section?.sectionTitle,
    });
    scrollToSection(section?.id);
    sectionLog({ chapter: chapter?.chapterTitle, section: section?.sectionTitle });
  };

  const closeAll = () => setOpen(false);

  return (
    <div
      className={`font-geist fixed ani inset-0 z-[100] flex items-center justify-center ${
        open ? (maximized ? "" : "bg-black/50 p-6") : "bg-transparent select-none pointer-events-none"
      }`}
    >
      <div
        className={`font-geist bg-general-background flex flex-col justify-start items-end overflow-hidden ani ${
          maximized
            ? "w-screen h-screen"
            : "w-[980px] max-w-full h-[700px] max-h-full rounded-[10px] shadow-lg outline outline-1 outline-offset-[-1px] outline-general-border"
        } ${open ? "opacity-100 blur-0" : "opacity-0 blur-sm scale-110 select-none pointer-events-none"}`}
      >
        <div className="self-stretch px-4 py-2.5 flex flex-col justify-between items-start">
          <div className="self-stretch inline-flex justify-between items-center gap-4">
            <div className="min-w-0 flex-1 flex items-center gap-2">
              {/* Maximized fills the whole screen, so the guide reads as a page of its
                  own - the product mark next to the title keeps it branded there. */}
              {maximized && (
                <img
                  alt=""
                  src={HWAI_ICON_URL}
                  className="shrink-0 size-5 object-contain select-none"
                />
              )}
              <div className="min-w-0 flex-1 text-left text-general-foreground text-base font-semibold leading-6 truncate">
                Help Guide {activedashboard?.dashboardname ? `· ${activedashboard.dashboardname}` : ""}
              </div>
            </div>
            <div className="flex justify-end items-center gap-3">
              <button type="button" onClick={() => setMaximized((v) => !v)} className="ani hover:opacity-70">
                {maximized ? (
                  <LuMinimize2 className="size-4 text-general-muted-foreground" />
                ) : (
                  <LuMaximize2 className="size-4 text-general-muted-foreground" />
                )}
              </button>
              <button type="button" onClick={closeAll} className="ani hover:opacity-70">
                <MdClose className="size-4 text-general-muted-foreground" />
              </button>
            </div>
          </div>
        </div>

        <div className="self-stretch border-t border-general-border flex-1 min-h-0 flex flex-col justify-start items-start overflow-hidden">
          <div className="self-stretch flex-1 min-h-0 flex justify-start items-stretch">
            <div className="app-scrollbar w-64 shrink-0 self-stretch p-3 flex flex-col justify-start items-start gap-2 overflow-y-auto">
              <div className="self-stretch min-h-9 px-3 py-2 bg-general-input rounded-lg shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] outline outline-1 outline-offset-[-1px] outline-general-border flex justify-start items-center gap-2">
                <LuSearch className="size-4 shrink-0 text-general-muted-foreground" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search guide"
                  className="flex-1 min-w-0 bg-transparent outline-none text-general-foreground placeholder:text-general-muted-foreground text-sm font-normal leading-5"
                />
              </div>

              <div className="self-stretch flex flex-col justify-start items-start gap-1">
                {filteredChapters.map((chapter) => {
                  const isOpen = !chapter?.collapsed || !!search.trim();
                  const isChapterSelected = selected?.chapterId === chapter?.id && !selected?.sectionId;
                  return (
                    <div key={chapter?.id} className="self-stretch flex flex-col justify-start items-start">
                      <NavRow
                        text={chapter?.chapterTitle}
                        iconUrl={chapter?.chapterIconUrl}
                        onClick={() => selectChapter(chapter)}
                        rowClassName={`group self-stretch min-h-8 px-2 py-1.5 rounded-md inline-flex justify-start items-center gap-2 cursor-pointer ani ${
                          isChapterSelected ? "bg-unofficial-accent-2" : "hover:bg-general-accent"
                        }`}
                        labelClassName="flex-1 min-w-0 text-left text-general-foreground text-sm font-medium leading-5 truncate"
                      >
                        {chapter?.sections?.length > 0 ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              selectChapter(chapter);
                            }}
                            className="w-5 shrink-0 p-0.5 flex flex-col justify-center items-center"
                          >
                            <ChevronRightIcon className={`size-3.5 text-general-muted-foreground ani ${isOpen ? "rotate-90" : ""}`} />
                          </button>
                        ) : (
                          <span className="w-5 shrink-0" />
                        )}
                      </NavRow>
                      {isOpen && chapter?.sections?.length > 0 && (
                        <div className="self-stretch pl-3 flex flex-col justify-start items-start">
                          {chapter.sections.map((section) => {
                            const isSectionSelected = selected?.chapterId === chapter?.id && selected?.sectionId === section?.id;
                            return (
                              <NavRow
                                key={section?.id}
                                text={section?.sectionTitle}
                                onClick={() => selectSection(chapter, section)}
                                rowClassName={`self-stretch min-h-8 px-2 py-1.5 rounded-md flex justify-start items-center gap-2 cursor-pointer ani ${
                                  isSectionSelected ? "bg-unofficial-accent-2" : "hover:bg-general-accent"
                                }`}
                                labelClassName="flex-1 min-w-0 text-left text-general-foreground text-sm font-normal leading-5 truncate"
                              >
                                <span className="w-5 shrink-0" />
                              </NavRow>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="w-px shrink-0 self-stretch bg-general-border" />

            <div className="flex-1 min-w-0 self-stretch flex flex-col justify-start items-start">
              <div className="self-stretch px-3 py-1.5 flex justify-between items-center gap-4">
                <div className="flex-1 min-w-0 flex justify-start items-center gap-1">
                  <span className="text-general-muted-foreground text-xs font-normal leading-4 truncate">
                    {selected?.chapterLabel || "Select a chapter"}
                  </span>
                  {selected?.sectionLabel && (
                    <>
                      <ChevronRightIcon className="size-3 shrink-0 text-general-muted-foreground" />
                      <span className="text-general-foreground text-xs font-normal leading-4 truncate">
                        {selected.sectionLabel}
                      </span>
                    </>
                  )}
                </div>
                <div className="shrink-0 flex justify-center items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setZoom((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP))}
                    className="min-w-7 min-h-7 p-1.5 rounded-lg hover:bg-general-accent ani flex justify-center items-center"
                  >
                    <LuMinus className="size-3.5 text-unofficial-ghost-foreground" />
                  </button>
                  <span className="text-center text-unofficial-ghost-foreground text-xs font-normal leading-4 w-8">
                    {zoom}%
                  </span>
                  <button
                    type="button"
                    onClick={() => setZoom((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP))}
                    className="min-w-7 min-h-7 p-1.5 rounded-lg hover:bg-general-accent ani flex justify-center items-center"
                  >
                    <LuPlus className="size-3.5 text-unofficial-ghost-foreground" />
                  </button>
                </div>
              </div>

              <div className="self-stretch h-px bg-general-border" />

              <div className="app-scrollbar relative self-stretch flex-1 min-h-0 px-8 py-6 bg-general-muted flex flex-col items-center overflow-y-auto">
                {!!maximizedImage && (
                  <div className="absolute top-0 left-0 z-10 w-full h-full backdrop-blur-md flex flex-col gap-3 px-6 py-5 bg-[#0000000f]">
                    <div className="w-full flex justify-between items-center">
                      <div className="font-semibold text-base flex flex-row items-center gap-1.5">
                        <MdOutlineImage className="text-xl" />
                        {maximizedImageLabel ? maximizedImageLabel : "Image Preview"}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setMaximizedImage(null);
                          setMaximizedImageLabel(null);
                          setMaximizedImageSize("large");
                        }}
                        className="ani hover:-translate-x-1"
                      >
                        <MdChevronLeft className="text-2xl" aria-label="Close preview" />
                      </button>
                    </div>
                    <TransformWrapper>
                      <div className="relative group w-full h-full flex items-center justify-center overflow-hidden">
                        <TransformComponent wrapperStyle={{ overflow: "visible" }}>
                          <img
                            src={maximizedImage}
                            style={{
                              maxWidth: `${maximizedImageSize === "small" ? "30%" : maximizedImageSize === "medium" ? "60%" : "100%"}`,
                            }}
                            className="cursor-pointer w-full h-full object-contain"
                            alt={maximizedImage || "Content image"}
                          />
                        </TransformComponent>
                        <ImageZoomControls />
                      </div>
                    </TransformWrapper>
                  </div>
                )}

                <div
                  className="w-full max-w-[1000px] shrink-0 relative bg-general-background rounded shadow-[0px_1px_3px_0px_rgba(0,0,0,0.08)] p-6"
                  style={{ transform: `scale(${zoom / 100})`, transformOrigin: "top center" }}
                >
                  {chapters.length === 0 && (
                    <div className="text-general-muted-foreground text-sm">No content available yet.</div>
                  )}
                  {chapters.map((chapter) => (
                    <div key={chapter?.id} className="flex flex-col gap-4 border-b border-general-border pb-4 pt-1">
                      <div ref={(el) => (sectionRefs.current[chapter?.id] = el)}>
                        <div className="w-full h-max text-general-foreground text-left shrink-0 font-bold underline text-lg flex flex-row items-start gap-2">
                          {chapter?.chapterTitle}
                        </div>
                        <ContentBlocks contents={chapter?.contents} onMaximizeImage={handleMaximizeImage} />
                      </div>
                      {chapter?.sections?.map((section) => (
                        <div key={section?.id} ref={(el) => (sectionRefs.current[section?.id] = el)}>
                          <div className="w-full h-max text-general-foreground text-left shrink-0 font-semibold text-base flex flex-row items-start gap-2">
                            {section?.sectionTitle}
                          </div>
                          <ContentBlocks contents={section?.contents} onMaximizeImage={handleMaximizeImage} />
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="self-stretch h-px bg-general-border" />

          {/* <div className="self-stretch px-4 py-3 flex justify-start items-center gap-4">
            <div className="flex-1 flex justify-end items-center gap-2">
              <span className="text-general-muted-foreground text-sm font-normal leading-5">
                Was this helpful?
              </span>
              <button
                type="button"
                onClick={() => submitHelpfulFeedback("yes")}
                disabled={Boolean(helpful)}
                className={`text-sm font-normal leading-5 underline ani hover:text-general-foreground disabled:hover:text-general-muted-foreground ${helpful === "yes" ? "text-general-foreground" : "text-general-muted-foreground"}`}
              >
                Yes
              </button>
              <button
                type="button"
                onClick={() => submitHelpfulFeedback("no")}
                disabled={Boolean(helpful)}
                className={`text-sm font-normal leading-5 underline ani hover:text-general-foreground disabled:hover:text-general-muted-foreground ${helpful === "no" ? "text-general-foreground" : "text-general-muted-foreground"}`}
              >
                No
              </button>
            </div>
          </div> */}
        </div>
      </div>
    </div>
  );
}
